import React, { useMemo } from 'react';
import { Challenge, Participant } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getExerciseForChallenge } from '@/lib/utils';

interface ChallengeCompletionProps {
  challenge: Challenge;
  participant: Participant;
  onClose: () => void;
  onViewChallenge: () => void;
}

const ChallengeCompletion: React.FC<ChallengeCompletionProps> = ({
  challenge,
  participant,
  onClose,
  onViewChallenge,
}) => {
  const exercise = getExerciseForChallenge(challenge);

  // Memoize all calculations to prevent re-renders causing shake
  const stats = useMemo(() => {
    // Calculate stats
    const totalSets = Object.values(participant.sets || {}).reduce(
      (total, sets) => total + sets.length,
      0
    );

    // Calculate streak (consecutive days with activity)
    const dailyLogEntries = Object.entries(participant.dailyLog || {})
      .filter(([_, amount]) => amount > 0)
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

    let currentStreak = 0;
    let maxStreak = 0;
    let tempStreak = 0;

    for (let i = 0; i < dailyLogEntries.length; i++) {
      if (i === 0) {
        tempStreak = 1;
      } else {
        const prevDate = new Date(dailyLogEntries[i - 1][0]);
        const currDate = new Date(dailyLogEntries[i][0]);
        const dayDiff = (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);

        if (dayDiff === 1) {
          tempStreak++;
        } else {
          maxStreak = Math.max(maxStreak, tempStreak);
          tempStreak = 1;
        }
      }
    }
    maxStreak = Math.max(maxStreak, tempStreak);
    currentStreak = tempStreak;

    // Find best single day
    const bestDay = Math.max(...Object.values(participant.dailyLog || {}));

    return {
      totalSets,
      currentStreak,
      bestDay,
    };
  }, [participant.sets, participant.dailyLog]);

  // Memoize the random message so it doesn't change on re-renders
  const randomMessage = useMemo(() => {
    const completionMessages = [
      '"You are a champion! Hasta la vista, baby!"',
      '"If it bleeds, we can kill it!" And you killed this challenge!',
      '"I\'ll be back..." for your next challenge!',
      '"Come with me if you want to lift!" You\'ve proven yourself!',
      '"Get to the chopper!" You\'ve earned your victory flight!',
    ];
    return completionMessages[Math.floor(Math.random() * completionMessages.length)];
  }, []); // Static messages, no dependencies needed

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      style={{ animation: 'none !important' }}
    >
      <Card
        className="relative w-full max-w-md bg-white shadow-2xl"
        style={{ animation: 'none !important', transform: 'none !important' }}
      >
        <div
          className="p-8 text-center"
          style={{ animation: 'none !important', transform: 'none !important' }}
        >
          {/* Challenge Completed Header with Logo-style Box */}
          <div className="mb-8">
            <div
              className="mb-4 mx-auto w-fit bg-slate-700 p-6 rounded-2xl border-2 border-green-500 flex flex-col items-center gap-4"
              style={{ animation: 'none !important', transform: 'none !important' }}
            >
              <div
                className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-xl overflow-hidden"
                style={{ animation: 'none !important', transform: 'none !important' }}
              >
                <img
                  src="/challenge-win.png"
                  alt="Challenge Complete"
                  className="w-full h-full object-cover"
                  style={{ animation: 'none !important', transform: 'none !important' }}
                />
              </div>
              <div
                className="text-green-500 text-3xl font-bold"
                style={{
                  fontFamily: 'Impact, "Arial Black", "Avenir Next Condensed Heavy", sans-serif',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  lineHeight: '0.85',
                  letterSpacing: '0.05em',
                  animation: 'none !important',
                  transform: 'none !important',
                }}
              >
                CHALLENGE COMPLETED!
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-2">{challenge.name}</p>
            <p className="text-sm text-gray-600">
              Target: {challenge.target.toLocaleString()} {exercise.unit}
            </p>
          </div>

          {/* Challenge Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Total Reps */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="text-2xl font-bold text-blue-700">
                {participant.totalAmount.toLocaleString()}
              </div>
              <div className="text-sm text-blue-600 font-medium">Total {exercise.unit}</div>
            </div>

            {/* Total Sets */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="text-2xl font-bold text-purple-700">{stats.totalSets}</div>
              <div className="text-sm text-purple-600 font-medium">Total Sets</div>
            </div>

            {/* Current Streak */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
              <div className="text-2xl font-bold text-orange-700">{stats.currentStreak}</div>
              <div className="text-sm text-orange-600 font-medium">Day Streak</div>
            </div>

            {/* Best Day */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="text-2xl font-bold text-green-700">{stats.bestDay}</div>
              <div className="text-sm text-green-600 font-medium">Best Day</div>
            </div>
          </div>

          {/* Motivational Message */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-700 italic">{randomMessage}</p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onViewChallenge}
              variant="primary"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              View Challenge Details
            </Button>
            <Button onClick={onClose} variant="secondary" className="w-full">
              Continue to Home
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export { ChallengeCompletion };
